import { LeadChannel } from 'models/Leads';
import {
  ConditionType,
  TriggerActionType,
  TriggerEventType,
  TriggerTimeCondition,
} from 'models/automation/Triggers';

/**
 * The trigger event types that should be displayed as options for users
 */
export const EVENT_TYPE_OPTIONS = [
  TriggerEventType.BLOCK_DELETED,
  TriggerEventType.BLOCK_UPDATED,
  TriggerEventType.BOOKING_CANCELLED,
  TriggerEventType.BOOKING_PAID_IN_FULL,
  TriggerEventType.BOOKING_UPDATED,
  TriggerEventType.JOB_ACCEPTED,
  TriggerEventType.JOB_CANCELLED,
  TriggerEventType.JOB_COMPLETED,
  TriggerEventType.JOB_CREATED,
  TriggerEventType.JOB_DECLINED,
  TriggerEventType.LEAD_DATES_CHANGED,
  TriggerEventType.LEAD_PROPERTY_CHANGED,
  TriggerEventType.NEW_BLOCKED_DATES,
  TriggerEventType.NEW_BOOKING_REQUEST,
  TriggerEventType.NEW_BOOKING,
  TriggerEventType.NEW_INBOX_MESSAGE,
  TriggerEventType.NEW_INQUIRY,
  TriggerEventType.NEW_PROPERTY,
  TriggerEventType.PAYMENT_FAILED,
  TriggerEventType.PRE_ARRIVAL_FORM_COMPLETED,
  TriggerEventType.RENTAL_AGREEMENT_SIGNED,
  TriggerEventType.UNIT_CHANGED,
  TriggerEventType.UPDATED_PROPERTY,
] as const;

/**
 * Map retrieves the correct set of condition types given an event type.
 */
export const EVENT_TYPE_TO_CONDITIONS_MAPPING: {
  [key in TriggerEventType]?: ConditionType[];
} = {
  [TriggerEventType.BOOKING_CANCELLED]: [
    ConditionType.CHANNEL,
    ConditionType.PROPERTY,
    ConditionType.GUESTS_NUMBER,
    ConditionType.STAY_LENGTH,
    ConditionType.AGREEMENT_SIGNED,
    ConditionType.BOOKING_PAID_IN_FULL,
    ConditionType.PRE_ARRIVAL_FORM_SUBMITTED,
  ],
  [TriggerEventType.NEW_BOOKING]: [
    ConditionType.CHANNEL,
    ConditionType.PROPERTY,
    ConditionType.GUESTS_NUMBER,
    ConditionType.STAY_LENGTH,
    ConditionType.AGREEMENT_SIGNED,
    ConditionType.BOOKING_PAID_IN_FULL,
    ConditionType.PRE_ARRIVAL_FORM_SUBMITTED,
  ],
  [TriggerEventType.NEW_INQUIRY]: [
    ConditionType.CHANNEL,
    ConditionType.PROPERTY,
    ConditionType.GUESTS_NUMBER,
    ConditionType.STAY_LENGTH,
  ],
  [TriggerEventType.BOOKING_PAID_IN_FULL]: [
    ConditionType.CHANNEL,
    ConditionType.PROPERTY,
    ConditionType.GUESTS_NUMBER,
    ConditionType.STAY_LENGTH,
    ConditionType.AGREEMENT_SIGNED,
    ConditionType.PRE_ARRIVAL_FORM_SUBMITTED,
  ],
  [TriggerEventType.PAYMENT_FAILED]: [
    ConditionType.CHANNEL,
    ConditionType.PROPERTY,
    ConditionType.GUESTS_NUMBER,
    ConditionType.STAY_LENGTH,
    ConditionType.AGREEMENT_SIGNED,
    ConditionType.BOOKING_PAID_IN_FULL,
    ConditionType.PRE_ARRIVAL_FORM_SUBMITTED,
  ],
  [TriggerEventType.NEW_BOOKING_REQUEST]: [
    ConditionType.CHANNEL,
    ConditionType.PROPERTY,
    ConditionType.GUESTS_NUMBER,
    ConditionType.STAY_LENGTH,
    ConditionType.AGREEMENT_SIGNED,
    ConditionType.BOOKING_PAID_IN_FULL,
    ConditionType.PRE_ARRIVAL_FORM_SUBMITTED,
  ],
  [TriggerEventType.BOOKING_UPDATED]: [
    ConditionType.CHANNEL,
    ConditionType.PROPERTY,
    ConditionType.GUESTS_NUMBER,
    ConditionType.STAY_LENGTH,
    ConditionType.AGREEMENT_SIGNED,
    ConditionType.BOOKING_PAID_IN_FULL,
    ConditionType.PRE_ARRIVAL_FORM_SUBMITTED,
  ],
  [TriggerEventType.LEAD_DATES_CHANGED]: [
    ConditionType.CHANNEL,
    ConditionType.PROPERTY,
    ConditionType.GUESTS_NUMBER,
    ConditionType.STAY_LENGTH,
    ConditionType.AGREEMENT_SIGNED,
    ConditionType.BOOKING_PAID_IN_FULL,
    ConditionType.PRE_ARRIVAL_FORM_SUBMITTED,
  ],
  [TriggerEventType.LEAD_PROPERTY_CHANGED]: [
    ConditionType.CHANNEL,
    ConditionType.PROPERTY,
    ConditionType.GUESTS_NUMBER,
    ConditionType.STAY_LENGTH,
    ConditionType.AGREEMENT_SIGNED,
    ConditionType.BOOKING_PAID_IN_FULL,
    ConditionType.PRE_ARRIVAL_FORM_SUBMITTED,
  ],
  [TriggerEventType.NEW_BLOCKED_DATES]: [ConditionType.PROPERTY],
  [TriggerEventType.BLOCK_DELETED]: [ConditionType.PROPERTY],
  [TriggerEventType.BLOCK_UPDATED]: [ConditionType.PROPERTY],
  [TriggerEventType.JOB_ACCEPTED]: [ConditionType.PROPERTY],
  [TriggerEventType.JOB_CANCELLED]: [ConditionType.PROPERTY],
  [TriggerEventType.JOB_COMPLETED]: [ConditionType.PROPERTY],
  [TriggerEventType.JOB_CREATED]: [ConditionType.PROPERTY],
  [TriggerEventType.JOB_DECLINED]: [ConditionType.PROPERTY],
  [TriggerEventType.NEW_INBOX_MESSAGE]: [ConditionType.PROPERTY],
  [TriggerEventType.NEW_PROPERTY]: [],
  [TriggerEventType.UPDATED_PROPERTY]: [ConditionType.PROPERTY],
  [TriggerEventType.PRE_ARRIVAL_FORM_COMPLETED]: [ConditionType.PROPERTY],
  [TriggerEventType.RENTAL_AGREEMENT_SIGNED]: [ConditionType.PROPERTY],
  [TriggerEventType.UNIT_CHANGED]: [
    ConditionType.CHANNEL,
    ConditionType.PROPERTY,
    ConditionType.GUESTS_NUMBER,
    ConditionType.STAY_LENGTH,
    ConditionType.AGREEMENT_SIGNED,
    ConditionType.BOOKING_PAID_IN_FULL,
    ConditionType.PRE_ARRIVAL_FORM_SUBMITTED,
  ],
};

/**
 * Map retrieves the correct set of condition types given a time condition option.
 */
export const TIME_CONDITION_TO_CONDITIONS_MAPPING: {
  [key in TriggerTimeCondition]: ConditionType[];
} = {
  [TriggerTimeCondition.CHECK_IN]: [
    ConditionType.CHANNEL,
    ConditionType.PROPERTY,
    ConditionType.GUESTS_NUMBER,
    ConditionType.STAY_LENGTH,
    ConditionType.AGREEMENT_SIGNED,
    ConditionType.BOOKING_PAID_IN_FULL,
    ConditionType.PRE_ARRIVAL_FORM_SUBMITTED,
  ],
  [TriggerTimeCondition.CHECK_OUT]: [
    ConditionType.CHANNEL,
    ConditionType.PROPERTY,
    ConditionType.GUESTS_NUMBER,
    ConditionType.STAY_LENGTH,
    ConditionType.AGREEMENT_SIGNED,
    ConditionType.BOOKING_PAID_IN_FULL,
    ConditionType.PRE_ARRIVAL_FORM_SUBMITTED,
  ],
  [TriggerTimeCondition.DAY_OF_WEEK]: [
    ConditionType.CHANNEL,
    ConditionType.PROPERTY,
    ConditionType.GUESTS_NUMBER,
    ConditionType.STAY_LENGTH,
    ConditionType.AGREEMENT_SIGNED,
    ConditionType.BOOKING_PAID_IN_FULL,
    ConditionType.PRE_ARRIVAL_FORM_SUBMITTED,
  ],
};

/**
 * List of the channels that the user can use in order to enable the "Send channel message" action.
 */
export const ALLOWED_CHANNELS_FOR_DIRECT_MESSAGE = [
  LeadChannel.AIRBNB,
  LeadChannel.BOOKING_COM,
  LeadChannel.VRBO,
];

/**
 * List of the channels that the user can use in order to enable the "Post review" action.
 */
export const ALLOWED_CHANNELS_FOR_POST_REVIEW = [
  LeadChannel.AIRBNB,
  LeadChannel.VRBO,
];

export enum ActionTypeFields {
  JOB_TIMING = 'JOB_TIMING',
  RECIPIENTS = 'RECIPIENTS',
  SERVICES = 'SERVICES',
  TEMPLATES = 'TEMPLATES',
}

export const ACTION_TYPE_TO_DEPENDENT_FIELDS_MAPPING: {
  [K in TriggerActionType]: ActionTypeFields[];
} = {
  [TriggerActionType.CREATE_JOB]: [
    ActionTypeFields.SERVICES,
    ActionTypeFields.JOB_TIMING,
  ],
  [TriggerActionType.CREATE_REVIEW]: [],
  [TriggerActionType.DIRECT_MESSAGE]: [ActionTypeFields.TEMPLATES],
  [TriggerActionType.EMAIL]: [
    ActionTypeFields.TEMPLATES,
    ActionTypeFields.RECIPIENTS,
  ],
  [TriggerActionType.SMS]: [
    ActionTypeFields.TEMPLATES,
    ActionTypeFields.RECIPIENTS,
  ],
  [TriggerActionType.WHATSAPP]: [
    ActionTypeFields.TEMPLATES,
    ActionTypeFields.RECIPIENTS,
  ],
};
